import React, { useContext, useMemo } from 'react';
import { has, pickBy } from 'lodash';
import qs from 'qs';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { SubscriptionsMainContext } from '../../../index';
import TableMassActions from '../../../../../../components/Table/MassActions';
import ServicesDropdown from '../../../../../../components/Dropdown/ServicesDropdown';
import { Head, Row, Cell } from '../../../../../../components/Table/Grid';

import {
  checkedMassAdd,
  checkedMassDelete,
  getDelaysFilter,
  getDelaysFilterError,
  getDelaysFilterSuccess,
  getServiceFilter, getServiceFilterError,
  getServiceFilterSuccess,
  modalOpen,
} from '../../../reducer';
import DropdownRequest from '../../../../../../components/Dropdown/Request';
import { subscriptionsGetDelays, subscriptionsGetService } from '../../../../../../services/endpoints/subscriptions';
import { STATUSES } from '../../../constants/statuses';
import * as modals from '../../../constants/modals';
import { massActions } from '../../../constants/actions';
import {
  getDelayFilterName,
  getSelectedServices,
  getServicesFilterName,
} from '../../../helpers';
import { getRouteLinkById, getSortColumnByName } from '../../../../../../utils/helper';
import SortIcon from '../../../../../../components/Table/SortIcon';

const SubscriptionsTableHeader = () => {
  const [store, dispatch] = useContext(SubscriptionsMainContext);
  const { darkMode } = useSelector((state) => state.general);

  const { isLoadingTable, configuration, checked, filters, delays, service, sort } = store;
  const { access } = configuration;

  const location = useLocation();
  const history = useHistory();
  const route = useMemo(() => getRouteLinkById(), [])
  const queryString = qs.parse(location.search.slice(1));

  const actions = useMemo(() => massActions.filter((action) => !!access[action.accessKey]), [access])
  const selectedServices = useMemo(() => getSelectedServices(service.data, filters.service), [service.data, filters.service])
  const delayFilterName = useMemo(() => getDelayFilterName(filters.delay), [filters.delay])
  const delayFilterIsActive = !!filters.delay;
  const servicesFilterName = useMemo(() => getServicesFilterName(filters.service), [filters.service])
  const servicesFilterIsActive = !!filters.service;

  const isCheckedMassAction = !!checked.length;
  const onChangeMassCheckbox = () => {
    if (isCheckedMassAction) {
      dispatch(checkedMassDelete())
      return;
    }
    dispatch(checkedMassAdd())
  }
  const onChangeMassAction = (action) => {
    switch (action.value) {
    case STATUSES.ACTIVE.code:
    case STATUSES.FINISHED.code:
    case STATUSES.PAUSE.code:
      dispatch(modalOpen(modals.CHANGE_MASS_STATUS, {
        status_code: action.value.toUpperCase(),
      }))
      break;
    default:
    }
  }
  const onRequestFiltersDelay = async () => {
    dispatch(getDelaysFilter())
    try {
      const response = await subscriptionsGetDelays(queryString)
      if (response.data?.delays) {
        const delaysList = response.data.delays.map((delay) => ({
          title: `${delay.title} (${delay.count})`,
          id: delay.delay,
        }))
        dispatch(getDelaysFilterSuccess(delaysList))
      }
    } catch (error) {
      dispatch(getDelaysFilterError())
    }
  }

  const onClearFilters = () => true
  const onRequestFiltersService = async () => {
    const queryString = qs.parse(location.search.slice(1));
    dispatch(getServiceFilter())
    try {
      const response = await subscriptionsGetService(queryString)
      if (response.data?.services) {
        const servicesList = response.data.services.map((service) => ({
          label: service.title,
          id: service.id,
          count: service.count,
        }))
        dispatch(getServiceFilterSuccess(servicesList))
      }
    } catch (error) {
      dispatch(getServiceFilterError())
    }
  }
  const onChangeSelectService = (options) => {
    const newServices = options.filter((option) => option.id !== null).map((opt) => opt.id);
    const queryString = qs.parse(location.search.slice(1));
    delete queryString.service;
    const queryObj = {
      service: newServices,
      ...queryString,
    };
    if (has(queryObj, 'page')) delete queryObj.page;
    history.push({ pathname: route.link, search: qs.stringify({ ...pickBy(queryObj) }) });
  };

  const sortId = useMemo(() => getSortColumnByName(sort.columns, 'id'), [sort.columns])
  const sortCreatedAt = useMemo(() => getSortColumnByName(sort.columns, 'created'), [sort.columns])
  const sortUpdatedAt = useMemo(() => getSortColumnByName(sort.columns, 'updated'), [sort.columns])
  const sortExpiryAt = useMemo(() => getSortColumnByName(sort.columns, 'expiry'), [sort.columns])
  const onChangeSort = (sortColumn) => {
    const srt = sortColumn.active === 'asc' ? sortColumn.desc : (sortColumn.active === 'desc' ? sortColumn.asc : sortColumn.asc)
    const queryString = qs.parse(location.search.slice(1));
    delete queryString.sort;
    const queryObj = {
      sort: srt,
      ...queryString,
    };
    if (has(queryObj, 'page')) delete queryObj.page;
    history.push({ pathname: route.link, search: qs.stringify({ ...pickBy(queryObj) }) });
  }

  return (
    <Head bg>
      <Row>
        {actions.length ? (
          <Cell height="md" borderBottom="xs" style={{ minWidth: 42, width: 42 }}>
            <div className="d-flex justify-center">
              <TableMassActions
                count={checked.length}
                counterTitle="subscriptions"
                checked={isCheckedMassAction}
                disabled={isLoadingTable}
                onChange={onChangeMassCheckbox}
              >
                <ServicesDropdown id="mass-actions" handleSelect={onChangeMassAction} bsSize="xsmall" title="Actions" items={actions} />
              </TableMassActions>
            </div>
          </Cell>
        ) : null }
        <Cell height="md" borderBottom="xs" paddingX={actions.length ? 'sm' : 'md'} style={{ minWidth: '50px', width: '50px' }} bold>
          <span onClick={() => onChangeSort(sortId)} className="cursor-pinter">
            ID
            {' '}
            <SortIcon sortObj={sortId} />
          </span>
        </Cell>
        <Cell height="md" borderBottom="xs" style={{ minWidth: 120 }} bold>User</Cell>
        <Cell height="md" borderBottom="xs" style={{ minWidth: 220 }} bold>Username</Cell>
        <Cell height="md" borderBottom="xs" style={{ minWidth: 120 }} bold>Quantity</Cell>
        <Cell height="md" borderBottom="xs" style={{ minWidth: 120 }} bold>New posts</Cell>
        <Cell height="md" borderBottom="xs" style={{ minWidth: 120 }} bold>Old posts</Cell>
        {access.delays
          ? (
            <Cell height="md" borderBottom="xs" style={{ minWidth: 120 }} bold>
              <div className="tickets__status">
                <DropdownRequest
                  title={delayFilterName}
                  items={delays.data}
                  queryRequest="delay"
                  onRequestFunc={onRequestFiltersDelay}
                  clearData={onClearFilters}
                  fetchItems={delays.isLoading}
                  active={delayFilterIsActive}
                  darkMode={darkMode}
                  pullRight
                />
              </div>
            </Cell>
          ) : null}
        {access.services
          ? (
            <Cell height="md" borderBottom="xs" style={{ minWidth: 120 }} bold>
              <div className="tickets__status">
                <DropdownRequest
                  title={servicesFilterName}
                  type="select"
                  items={service.data}
                  queryRequest="service"
                  onRequestFunc={onRequestFiltersService}
                  onChangeSelect={onChangeSelectService}
                  clearData={onClearFilters}
                  fetchItems={service.isLoading}
                  active={servicesFilterIsActive}
                  value={selectedServices}
                  darkMode={darkMode}
                  pullRight
                  isSorted
                />
              </div>
            </Cell>
          ) : null}
        <Cell height="md" borderBottom="xs" style={{ minWidth: 120 }} bold>Status</Cell>
        <Cell height="md" borderBottom="xs" style={{ minWidth: 86 }} bold>
          <span onClick={() => onChangeSort(sortCreatedAt)} className="cursor-pinter">
            Created
            {' '}
            <SortIcon sortObj={sortCreatedAt} />
          </span>
        </Cell>
        <Cell height="md" borderBottom="xs" style={{ minWidth: 86 }} bold>
          <span onClick={() => onChangeSort(sortUpdatedAt)} className="cursor-pinter">
            Updated
            {' '}
            <SortIcon sortObj={sortUpdatedAt} />
          </span>
        </Cell>
        <Cell height="md" borderBottom="xs" style={{ minWidth: 86 }} bold>
          <span onClick={() => onChangeSort(sortExpiryAt)} className="cursor-pinter">
            Expiry
            {' '}
            <SortIcon sortObj={sortExpiryAt} />
          </span>
        </Cell>
        <Cell height="md" borderBottom="xs" style={{ minWidth: 100 }} />
      </Row>
    </Head>
  );
};

SubscriptionsTableHeader.propTypes = {

};

export default SubscriptionsTableHeader;
